'use client'

import Image from "next/image";
import Link from "@/components/ui/Link";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";

interface AcademicPublishedExpertiseData {
  anchor_title?: string;
  heading?: {
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  button?: {
    label?: string;
    href?: string;
  };
}

export const fields = [
  "anchor_title",
  "heading.title",
  "text.text",
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  "button.label",
  "button.href",
] as const;

export default function AcademicPublishedExpertise({ data }: { data?: AcademicPublishedExpertiseData }) {
  const imageUrl = getReadableImageUrl(
    data?.image?.image_file?.image?.id || "",
    data?.image?.image_file?.image?.filename_download || ""
  );

  const sectionId = (data?.anchor_title || 'AcademicPublishedExpertise')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .replace(/-{2,}/g, '-');

  return (
    <section id={sectionId} className="AcademicPublishedExpertise pb-16 md:py-16">
      <div className="container">
        <div className="flex flex-col lg:flex-row lg:space-x-8 mx-auto justify-center md:gap-16 lg:items-stretch">
          <div className="order-1 lg:order-1 w-full lg:w-1/2 mb-8 lg:mb-0">
            {data?.image && imageUrl && (
              <div className="relative w-full h-full min-h-[250px] lg:min-h-[400px] overflow-hidden">
                <Image
                  src={imageUrl}
                  alt={data.image.alt || ""}
                  fill
                  quality={95}
                  loading="lazy"
                  className="object-cover"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                />
              </div>
            )}
          </div>
          <div className="order-2 lg:order-2 w-full lg:w-1/2 mb-8 lg:mb-0 relative flex flex-wrap content-between">
            <div>
              {data?.heading && (
                <h2 className="font-sans text-xl md:text-[1.75rem] font-semibold leading-8 md:leading-10 tracking-[0.07rem] uppercase text-[#030303] mb-2">{data.heading.title}</h2>
              )}
              {data?.text && (
                <div
                  className="grid gap-6 font-sans text-base font-normal leading-7 text-black"
                  dangerouslySetInnerHTML={sanitizeHTML(data.text.text || "")}
                />
              )}
            </div>
            <div className="w-full">
              {data?.button?.href && (
                <div className="mt-6 text-left w-full">
                  <Link className="btn" href={data.button.href}>
                    {data.button.label}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
